import { Link } from 'react-router-dom';

import { Detail, Field, FieldList, Notice, Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import { useProjectName } from '../../../global/contexts/lookups/lookups';
import { useTier } from '../contexts/tiers';
import { AgreeTierButton } from '../../breakdown/layouts/agree-tier';
import Authorship from '../../audit/layouts/authorship';
import { useTierAuthorship } from '../../audit/contexts/authorship';
import { BREAKDOWN_STATE_LABEL, TIER_PHASE_LABEL } from '../utils/form';

import '../../../common/components/detail/styles/detail.css';

// Reading a tier without holding `tiers:write`.
//
// THE THREE AXES ARE SHOWN AS THREE, which is the whole design of this screen:
//
//   breakdown_state  is the scope agreed?          draft · agreed · legacy
//   phase            where is the project's life?  discovery … operation
//   tier_state       is it being worked?           active · closed
//
// They move independently and mean different things. One label called "Estado"
// would be the fastest way to make this page lie.

const BREAKDOWN_TONE = { agreed: 'active', draft: 'warn', legacy: 'neutral' };

export default function TierDetail({ tierId }) {
    const { data, isLoading, error } = useTier(tierId);
    const projectName = useProjectName();

    if (isLoading) return <p className="client-form__loading">Cargando tier...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.status === 404
                    ? 'Este tier no está disponible: no existe, o queda fuera de tu alcance.'
                    : error.message || 'No se pudo cargar el tier'}
            </p>
        );
    }

    const tier = data?.tier ?? data;
    const breakdown = BREAKDOWN_STATE_LABEL[tier.breakdown_state] ?? tier.breakdown_state;
    const phase = TIER_PHASE_LABEL[tier.phase] ?? tier.phase;
    const legacy = tier.breakdown_state === 'legacy';
    const frozen = !!tier.frozen;

    return (
        <section className="detail-page">
            <header className="detail-head">
                <div className="detail-head__text">
                    <h2 className="detail-head__title">{tier.name}</h2>
                    <div className="detail-head__tags">
                        <Tag>Tier #{tier.id}</Tag>
                        {breakdown && (
                            <Tag tone={BREAKDOWN_TONE[tier.breakdown_state] ?? 'neutral'} title="Preparación del desglose">
                                Desglose: {breakdown}
                            </Tag>
                        )}
                        {phase && <Tag title="Fase del proyecto">Fase: {phase}</Tag>}
                        {tier.tier_state && (
                            <Tag tone={tier.tier_state === 'active' ? 'active' : 'neutral'} title="Estado del tier">
                                {tier.tier_state === 'active' ? 'Activo' : 'Cerrado'}
                            </Tag>
                        )}
                        {tier.breakdown_revision != null && <Tag>Revisión {tier.breakdown_revision}</Tag>}
                    </div>
                </div>

                <div className="detail-head__actions">
                    {/* Back to where the list lives now, keeping the project. */}
                    <Link
                        to={`/admin/development/tiers${tier.project_id ? `?project=${tier.project_id}` : ''}`}
                        className="detail-head__action detail-head__action--quiet"
                    >
                        Volver
                    </Link>
                    <Link
                        to={`/admin/development/tareas?view=list&tier=${tier.id}`}
                        className="detail-head__action detail-head__action--quiet"
                    >
                        Ver sus tareas
                    </Link>
                    <Gate domain="breakdown:read">
                        <Link
                            to={`/admin/tiers/${tier.id}/desglose`}
                            className="detail-head__action detail-head__action--quiet"
                        >
                            Desglose
                        </Link>
                    </Gate>
                    <Gate domain="tiers:write">
                        <Link
                            to={`/admin/tiers/${tier.id}/edit`}
                            className={`detail-head__action${tier.breakdown_state === 'draft' ? ' detail-head__action--quiet' : ''}`}
                        >
                            Editar
                        </Link>
                    </Gate>
                    {/* A draft's next step, so it takes the primary slot. The
                        modal reads the breakdown itself: revision and what is
                        missing are not in this detail. */}
                    {tier.breakdown_state === 'draft' && (
                        <Gate domain="tiers:agree">
                            <AgreeTierButton
                                tierId={tier.id}
                                className="detail-head__action"
                                label="Acordar tier"
                            />
                        </Gate>
                    )}
                </div>
            </header>

            {/* NO SE INFIERE UN PLAN. A legacy tier did not come out of one, and
                filling the gap with "the most likely" would hand somebody a
                context nobody approved — the expensive error, because it is the
                one that does not look like an error. */}
            {legacy && (
                <Notice>
                    <strong>Origen de planeación no registrado.</strong> Este tier es anterior a
                    la planeación canónica, así que no salió de ninguna versión de plan y no se
                    le atribuye una. Enlazarlo es una operación aparte, y se hace una sola vez.
                </Notice>
            )}

            {frozen && (
                <Notice tone="warn">
                    El tier tiene <strong>tareas autorizadas</strong>, así que su contenido y sus
                    dependencias quedaron congelados y no cambian por ninguna puerta. El orden,
                    el estado del tier y el del registro siguen abiertos.
                </Notice>
            )}

            {tier.breakdown_state === 'agreed' && (
                <Notice tone="accent">
                    El alcance de este tier está <strong>acordado</strong>. Editar su contenido lo
                    devuelve a borrador y hace falta acordarlo de nuevo.
                </Notice>
            )}

            <TierAuthorship tierId={tier.id} />

            <Detail>
                {/* `GET /admin/tiers/{id}` answers `project_id` and no project
                    block, so `tier.project.name` was never there and the field
                    always read `#2`. The name comes from the shared catalog. */}
                <Field
                    label="Proyecto"
                    value={tier.project_id ? (
                        <Link to={`/admin/projects/${tier.project_id}`}>
                            {tier.project?.name ?? projectName(tier.project_id)}
                        </Link>
                    ) : null}
                />
                <Field label="Orden dentro del proyecto" value={tier.sort_order} />

                <Field
                    label="Origen"
                    value={tier.plan?.name
                        ? `${tier.plan.name} · versión ${tier.plan_version?.number ?? '?'}`
                        : null}
                    empty={legacy ? 'No registrado (tier legado)' : 'Sin versión de plan enlazada'}
                />
                <Field
                    label="Estado del registro"
                    value={tier.status === 2 ? 'Activo' : 'Inactivo'}
                />

                <Field label="Descripción" value={tier.description} empty="Sin descripción — hace falta para acordar" wide markdown />
                <Field
                    label="Resultado esperado"
                    value={tier.expected_outcome}
                    empty="Sin registrar — hace falta para acordar"
                    wide
                    markdown
                />
                <FieldList
                    label="Dudas abiertas"
                    items={tier.open_questions}
                    empty="Ninguna — el tier está listo por este lado"
                />
            </Detail>

            {tier.hidden_dependencies > 0 && (
                <Notice>
                    Depende además de <strong>{tier.hidden_dependencies}</strong>
                    {' '}tier{tier.hidden_dependencies === 1 ? '' : 's'} fuera de tu alcance.
                    {' '}Una dependencia oculta con problemas <strong>bloquea el acuerdo igual</strong>,
                    contada sin nombrarse.
                </Notice>
            )}
        </section>
    );
}

// Who created the tier, who edited its draft last and who agreed its scope.
// The tier table records none of it; the event log does.
function TierAuthorship({ tierId }) {
    const { authorship, readable, isLoading } = useTierAuthorship(tierId);

    return (
        <Authorship
            readable={readable}
            isLoading={isLoading}
            rows={[
                { label: 'Creado por', actor: authorship.created },
                { label: 'Última edición', actor: authorship.edited },
                { label: 'Acordado por', actor: authorship.agreed },
            ]}
        />
    );
}
